// Search utilities for indicator search functionality
import { dataCategories } from '../config/categories';

/**
 * Build searchable index from all indicators across categories
 * @returns {Array} Array of searchable indicator objects
 */
export function buildSearchIndex() {
  const index = [];

  dataCategories.forEach(category => {
    category.indicators.forEach(indicatorName => {
      index.push({
        id: generateId(indicatorName, category.id),
        name: indicatorName,
        category: category.name,
        categoryId: category.id,
        icon: category.icon,
        keywords: extractKeywords(indicatorName),
        searchText: indicatorName.toLowerCase()
      });
    });
  });

  return index;
}

/**
 * Build searchable index from manifest data
 * @param {Object} manifest
 * @param {Array} categories
 * @returns {Array}
 */
export function buildSearchIndexFromManifest(manifest, categories = []) {
  if (!manifest?.indicators || !Array.isArray(manifest.indicators)) return [];

  const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
  const index = [];
  const seen = new Set();
  const normalizeKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  manifest.indicators.forEach(indicator => {
    const categoryId = (indicator.sectorId || '').toLowerCase();
    if (!categoryId || !indicator.name) return;

    const key = `${categoryId}|${normalizeKey(indicator.name)}`;
    if (seen.has(key)) return;
    seen.add(key);

    const category = categoryMap.get(categoryId);
    index.push({
      id: generateId(indicator.name, categoryId),
      name: indicator.name,
      category: category?.name || indicator.sector || categoryId,
      categoryId,
      icon: category?.icon || '*',
      keywords: extractKeywords(indicator.name),
      searchText: indicator.name.toLowerCase()
    });
  });

  return index;
}

/**
 * Generate unique ID for indicator
 * @param {string} indicatorName
 * @param {string} categoryId
 * @returns {string}
 */
function generateId(indicatorName, categoryId) {
  return `${categoryId}-${indicatorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * Extract keywords from indicator name for better search
 * @param {string} indicatorName
 * @returns {Array} Array of keywords
 */
function extractKeywords(indicatorName) {
  // Remove special characters and split into words
  const words = indicatorName
    .toLowerCase()
    .replace(/[()%]/g, '')
    .split(/[\s-]+/)
    .filter(w => w.length > 2); // Keep words longer than 2 chars

  return [...new Set(words)]; // Remove duplicates
}

/**
 * Fuzzy search through indicators
 * @param {string} query - Search query
 * @param {Array} searchIndex - Pre-built search index
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} Matching indicators
 */
export function searchIndicators(query, searchIndex, maxResults = 20) {
  if (!query || query.trim().length === 0) return [];

  const searchTerm = query.trim().toLowerCase();
  const results = [];

  searchIndex.forEach(item => {
    let score = 0;

    // Exact match (highest priority)
    if (item.searchText === searchTerm) {
      score = 1000;
    }
    // Starts with query (high priority)
    else if (item.searchText.startsWith(searchTerm)) {
      score = 500;
    }
    // Contains query (medium priority)
    else if (item.searchText.includes(searchTerm)) {
      score = 100;
    }
    // Keyword match (lower priority)
    else if (item.keywords.some(kw => kw.includes(searchTerm))) {
      score = 50;
    }
    // Fuzzy match - all words present
    else {
      const queryWords = searchTerm.split(/\s+/);
      const matchedWords = queryWords.filter(qw =>
        item.searchText.includes(qw) || item.keywords.some(kw => kw.includes(qw))
      );

      if (matchedWords.length > 0) {
        score = matchedWords.length * 10;
      }
    }

    if (score > 0) {
      results.push({ ...item, score });
    }
  });

  // Sort by score (descending) and return top results
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}

/**
 * Get all indicators for a specific category
 * @param {string} categoryId
 * @returns {Array} Array of indicator names
 */
export function getIndicatorsByCategory(categoryId) {
  const category = dataCategories.find(c => c.id === categoryId);
  return category ? category.indicators : [];
}

/**
 * Get category information by ID
 * @param {string} categoryId
 * @returns {Object} Category object
 */
export function getCategoryById(categoryId) {
  return dataCategories.find(c => c.id === categoryId);
}
